// Documento tributario en la interfaz, AGNÓSTICO del motor: se consume el estado de dominio que entrega
// la API (`operacional`, C-6), nunca se deriva ni se condiciona por proveedor (MadeciNative, Timbrado).
// El frontend no habla con el SII ni con Maullín. Un conflicto DNK solo se informa: resolverlo exige
// `dte.envio.conflicto.resolver` y la API no expone esa acción en este slice, así que no hay botón.
import { ImprimirVale, nombreMotivoVale } from '../ventas/ValeVenta';
import { Modal } from '../comun/Modal';
import { MOTIVOS_VALE, type MotivoVale } from '../api/contratos';
import { pesos } from '../comun/formato';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import { claves, useDocumentacion, useEstadoDte } from '../api/consultas';
import type { EstadoDte as EstadoDteApi, Nota } from '../api/contratos';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { useSesion } from '../sesion/SesionContexto';

interface Presentacion {
  texto: string;
  tono: 'ok' | 'aviso' | 'malo' | 'neutro' | 'info' | 'apagado';
  explicacion: string;
}

/** Estados operacionales (nombres de H4, derivados por el backend) → palabras del operador. */
export const ESTADO_DTE: Record<string, Presentacion> = {
  SOLICITADO: {
    texto: 'Pendiente (generando)',
    tono: 'neutro',
    explicacion: 'El documento fue solicitado; se genera y firma fuera de la caja.',
  },
  FALLIDO: {
    texto: 'Falló la generación',
    tono: 'malo',
    explicacion: 'La generación falló; administración puede reintentarla.',
  },
  ANULADO_SIN_EMISION: {
    texto: 'Anulado (sin emitir)',
    tono: 'apagado',
    explicacion: 'Se anuló antes de tener folio.',
  },
  ANULADO_PRE_ENVIO: {
    texto: 'Anulado antes del envío',
    tono: 'apagado',
    explicacion: 'Se anuló antes de salir al SII; su folio no se reutiliza.',
  },
  EN_CONFLICTO: {
    texto: 'En conflicto',
    tono: 'malo',
    explicacion: 'Lo emitido no corresponde a lo pedido. Requiere revisión.',
  },
  PENDIENTE_ENVIO: {
    texto: 'Sellado · pendiente de envío',
    tono: 'aviso',
    explicacion: 'Firmado; espera su envío al SII.',
  },
  ENVIANDO: { texto: 'Enviando al SII', tono: 'aviso', explicacion: 'Se está enviando al SII.' },
  TRACKID_ASIGNADO: {
    texto: 'Esperando SII',
    tono: 'aviso',
    explicacion: 'El SII recibió el envío (TrackID) y aún no informa el resultado.',
  },
  PROCESANDO: {
    texto: 'Esperando SII (procesando)',
    tono: 'aviso',
    explicacion: 'El SII lo está procesando. Se consulta solo.',
  },
  ACEPTADO: { texto: 'Aceptado', tono: 'ok', explicacion: 'Aceptado por el SII.' },
  ACEPTADO_CON_REPAROS: {
    texto: 'Aceptado con reparos',
    tono: 'aviso',
    explicacion: 'Aceptado por el SII con observaciones.',
  },
  RECHAZADO: {
    texto: 'Rechazado',
    tono: 'malo',
    explicacion: 'El SII rechazó el documento. Se corrige con un documento nuevo.',
  },
  ENVIO_RECHAZADO: {
    texto: 'Envío rechazado',
    tono: 'malo',
    explicacion: 'El SII rechazó el envío. Avise a administración.',
  },
  REQUIERE_CONCILIACION: {
    texto: 'Requiere conciliación',
    tono: 'malo',
    explicacion:
      'No se sabe con certeza si el SII lo recibió; se concilia antes de reenviar (nunca a mano).',
  },
  CONFLICTO_DTE_SII: {
    texto: 'Conflicto con el SII',
    tono: 'malo',
    explicacion: 'El SII informa que los datos no coinciden. Requiere revisión.',
  },
  CONFLICTO: {
    texto: 'Conflicto con el SII',
    tono: 'malo',
    explicacion: 'El SII informa que los datos no coinciden. Requiere revisión.',
  },
};

export function InsigniaDte({ operacional }: { operacional: string }) {
  const p = ESTADO_DTE[operacional] ?? {
    texto: operacional,
    tono: 'neutro',
    explicacion: operacional,
  };
  return (
    <span className={`insignia tono-${p.tono}`} data-dte={operacional} title={p.explicacion}>
      {p.texto}
    </span>
  );
}

const TIPO_DTE: Record<number, string> = {
  33: 'Factura electrónica',
  34: 'Factura exenta',
  39: 'Boleta electrónica',
  41: 'Boleta exenta',
  52: 'Guía de despacho',
  56: 'Nota de débito',
  61: 'Nota de crédito',
};
export const nombreTipoDte = (t: number | null | undefined) =>
  t ? (TIPO_DTE[t] ?? `Tipo ${t}`) : '—';

/** El estado de UN documento: insignia, folio, TrackID y, si hay conflicto, el aviso y quién resuelve. */
export function DetalleDte({ e }: { e: EstadoDteApi }) {
  const p = ESTADO_DTE[e.operacional];
  const conflicto = ['CONFLICTO_DTE_SII', 'CONFLICTO', 'EN_CONFLICTO'].includes(e.operacional);
  return (
    <div className="dte-detalle" data-documento-dte={e.documentoId}>
      <div className="fila-datos">
        <strong>{nombreTipoDte(e.tipoDte)}</strong>
        <InsigniaDte operacional={e.operacional} />
        <span>Folio {e.folio ?? '— (aún sin folio)'}</span>
        <span>TrackID {e.trackId ?? '—'}</span>
      </div>
      {p && !conflicto && <p className="ayuda">{p.explicacion}</p>}
      {conflicto && (
        <div className="aviso-linea tono-malo" role="alert" data-conflicto-sii>
          <strong>El SII informa que los datos no coinciden. Requiere revisión.</strong> La marca de
          resuelto la pone solo una persona autorizada (permiso{' '}
          <code>dte.envio.conflicto.resolver</code>); esta pantalla no la ofrece.
        </div>
      )}
    </div>
  );
}

export function EstadoDteDocumento({
  empresaId,
  documentoId,
}: {
  empresaId: string;
  documentoId: string;
}) {
  const q = useEstadoDte(empresaId, documentoId);
  if (q.isError) return <PanelError error={q.error} />;
  if (!q.data) return <span className="apagado">Cargando estado tributario…</span>;
  return <DetalleDte e={q.data} />;
}

/**
 * Documento tributario de una nota: sus documentos con el estado de cada uno y, con `dte.solicitar`,
 * pedir el documento de lo que falta (el backend decide tipo, líneas y si procede).
 */
export function PanelDocumentoTributario({ nota }: { nota: Nota }) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const confirmada = ['CONFIRMADA', 'CERRADA_CORTA'].includes(nota.estado);
  const doc = useDocumentacion(empresaId, nota.notaId, confirmada && puede('dte.ver'));
  const qc = useQueryClient();
  const intentos = useIntentos();
  const [pidiendo, setPidiendo] = useState(false);
  const [imprimirVale, setImprimirVale] = useState<string | null>(null);
  const [pasarAVale, setPasarAVale] = useState<{
    documentacionId: string;
    folio: number | null;
  } | null>(null);
  const [anularDuplicada, setAnularDuplicada] = useState<string | null>(null);
  const documentar = useMutation({
    mutationFn: () => {
      const cuerpo = {};
      return api.post(`/ventas/notas/${nota.notaId}/documentar`, cuerpo, {
        idempotencia: intentos.clave('documentar', { notaId: nota.notaId }),
      });
    },
    onSuccess: async () => {
      intentos.cerrar();
      setPidiendo(false);
      await Promise.all([
        qc.invalidateQueries({ queryKey: claves.documentacion(empresaId, nota.notaId) }),
        qc.invalidateQueries({ queryKey: claves.nota(empresaId, nota.notaId) }),
      ]);
    },
    onError: (e) => intentos.cerrar(e),
  });
  if (!confirmada) return null;
  const pendientes = doc.data?.lineas.some((l) => l.documentado !== l.pedido) ?? false;
  // 0121: la venta se puede anular entera si todos sus documentos vigentes siguen sin salir al SII
  // (el vale de venta no va al SII: siempre se puede).
  const vigentes = doc.data?.documentos.filter((d) => d.estado === 'VIGENTE') ?? [];
  const sinEnviar =
    vigentes.length > 0 &&
    vigentes.every(
      (d) =>
        d.vale ||
        (d.dte &&
          d.dte.estado === 'EMITIDO' &&
          d.dte.estadoEnvio === 'PENDIENTE' &&
          d.dte.estadoSii === 'SIN_RESPUESTA'),
    );
  const soloVales = vigentes.length > 0 && vigentes.every((d) => !d.dte);
  return (
    <section className="panel" aria-label="Documento tributario">
      <h3>Documento tributario</h3>
      {!puede('dte.ver') ? (
        <p className="apagado">Sin permiso para ver documentos tributarios.</p>
      ) : doc.isError ? (
        <PanelError error={doc.error} />
      ) : !doc.data ? (
        <p className="apagado">Cargando…</p>
      ) : (
        <>
          {doc.data.documentos.length === 0 && (
            <p className="apagado">La nota todavía no tiene documento tributario.</p>
          )}
          {doc.data.documentos.map((d) => (
            <div key={d.documentacionId} className="dte-bloque">
              {d.dte ? (
                <>
                  <EstadoDteDocumento empresaId={empresaId} documentoId={d.dte.documentoId} />
                  {d.tipoDocumento === 'BOLETA' &&
                    d.estado === 'VIGENTE' &&
                    d.dte.estado === 'EMITIDO' &&
                    d.dte.estadoEnvio === 'PENDIENTE' &&
                    d.dte.estadoSii === 'SIN_RESPUESTA' &&
                    puede('ventas.vale.pasar_boleta') && (
                      <button
                        type="button"
                        className="enlace"
                        data-pasar-a-vale={d.documentacionId}
                        onClick={() =>
                          setPasarAVale({ documentacionId: d.documentacionId, folio: d.dte!.folio })
                        }
                      >
                        Pasar a vale
                      </button>
                    )}
                </>
              ) : d.vale ? (
                <span data-vale-nota={d.vale.numero}>
                  <b>Vale N° {d.vale.numero}</b> · {nombreMotivoVale(d.vale.motivo)} ·{' '}
                  {pesos(d.vale.total)}{' '}
                  <span className="apagado">(documento interno, no tributario)</span>{' '}
                  <button
                    type="button"
                    className="enlace"
                    onClick={() => setImprimirVale(d.vale!.id)}
                  >
                    Reimprimir
                  </button>
                </span>
              ) : (
                <span className="apagado">Sin documento tributario asociado</span>
              )}
              <span className="apagado">
                Líneas {d.lineas.join(', ')}
                {d.estado !== 'VIGENTE' ? ` · documentación ${d.estado.toLowerCase()}` : ''}
              </span>
            </div>
          ))}
        </>
      )}
      {puede('dte.solicitar') && (doc.data ? pendientes : false) && (
        <div className="acciones">
          <button
            type="button"
            disabled={documentar.isPending}
            onClick={() => (pidiendo ? documentar.mutate() : setPidiendo(true))}
          >
            {documentar.isPending
              ? 'Solicitando…'
              : pidiendo
                ? 'Confirmar: solicitar documento tributario'
                : 'Solicitar documento tributario'}
          </button>
          {pidiendo && !documentar.isPending && (
            <button type="button" onClick={() => setPidiendo(false)}>
              Cancelar
            </button>
          )}
        </div>
      )}
      {sinEnviar && puede('ventas.venta.anular_duplicada') && (
        <div className="acciones">
          <button
            type="button"
            data-anular-duplicada={nota.notaId}
            title={
              soloVales
                ? 'Volvió la mercadería: anula el vale, devuelve la mercadería a la bodega y anula el recibo (si tiene) y la nota'
                : 'La misma venta se registró dos veces: anula el documento (no salió al SII), devuelve la mercadería, anula el recibo y la nota'
            }
            onClick={() =>
              setAnularDuplicada(
                vigentes
                  .map((d) =>
                    d.vale
                      ? `Vale N° ${d.vale.numero}`
                      : `${d.tipoDocumento === 'FACTURA' ? 'Factura' : 'Boleta'} N° ${d.dte?.folio ?? ''}`,
                  )
                  .join(', '),
              )
            }
          >
            {soloVales ? 'Anular vale' : 'Anular venta duplicada'}
          </button>
        </div>
      )}
      {!!documentar.error && <PanelError error={documentar.error} />}
      {anularDuplicada && (
        <AnularVentaDuplicada
          notaId={nota.notaId}
          documento={anularDuplicada}
          vale={soloVales}
          alCerrar={() => setAnularDuplicada(null)}
          alListo={async () => {
            setAnularDuplicada(null);
            await Promise.all([
              qc.invalidateQueries({ queryKey: claves.documentacion(empresaId, nota.notaId) }),
              qc.invalidateQueries({ queryKey: claves.nota(empresaId, nota.notaId) }),
            ]);
          }}
        />
      )}
      {imprimirVale && (
        <ImprimirVale valeId={imprimirVale} alTerminar={() => setImprimirVale(null)} />
      )}
      {pasarAVale && (
        <PasarAVale
          documentacionId={pasarAVale.documentacionId}
          folio={pasarAVale.folio}
          alCerrar={() => setPasarAVale(null)}
          alListo={async (valeId) => {
            setPasarAVale(null);
            await Promise.all([
              qc.invalidateQueries({ queryKey: claves.documentacion(empresaId, nota.notaId) }),
              qc.invalidateQueries({ queryKey: claves.nota(empresaId, nota.notaId) }),
            ]);
            if (valeId) setImprimirVale(valeId);
          }}
        />
      )}
    </section>
  );
}

/**
 * 0110 · pasar a vale una boleta que aún no sale al SII (como «Pasar a vale» de BotiOS): se anula antes
 * del envío, el pago de la venta pasa al vale y, si se marca, la próxima boleta reutiliza su folio.
 */
export function PasarAVale({
  documentacionId,
  folio,
  alCerrar,
  alListo,
}: {
  documentacionId: string;
  folio: number | null;
  alCerrar: () => void;
  alListo: (valeId: string | null) => void;
}) {
  const intentos = useIntentos();
  const [motivo, setMotivo] = useState('');
  const [valeMotivo, setValeMotivo] = useState<MotivoVale>('VENTA');
  const [reutilizar, setReutilizar] = useState(true);
  const [noEntregada, setNoEntregada] = useState(false);
  const pasar = useMutation({
    mutationFn: () => {
      const cuerpo = {
        motivo: motivo.trim(),
        valeMotivo,
        reutilizarFolio: reutilizar,
        confirmoNoEntregado: reutilizar && noEntregada,
      };
      return api.post<{ estado: string; valeId?: string; valeNumero?: string }>(
        `/ventas/documentaciones/${documentacionId}/pasar-a-vale`,
        cuerpo,
        { idempotencia: intentos.clave('pasar-a-vale', { documentacionId, ...cuerpo }) },
      );
    },
    onSuccess: (r) => {
      intentos.cerrar();
      if (r.estado === 'COMPLETADO') alListo(r.valeId ?? null);
    },
    onError: (e) => intentos.cerrar(e),
  });
  // Como BotiOS: reutilizar el folio exige confirmar que el cliente no se llevó la boleta.
  const valido = motivo.trim().length >= 3 && (!reutilizar || noEntregada);
  const r = pasar.data;
  return (
    <Modal
      titulo="Pasar la boleta a vale"
      alCerrar={alCerrar}
      pie={
        <div className="acciones">
          <button type="button" onClick={alCerrar}>
            Cancelar (Esc)
          </button>
          <button
            type="button"
            className="primario"
            disabled={!valido || pasar.isPending}
            onClick={() => pasar.mutate()}
          >
            {pasar.isPending ? 'Pasando…' : 'Pasar a vale'}
          </button>
        </div>
      }
    >
      <p>
        La boleta{folio ? ` N° ${folio}` : ''} todavía no sale al SII: se anula antes del envío y la
        venta queda como <b>vale de venta</b>. El pago pasa al vale; el stock no cambia.
      </p>
      <label className="campo">
        <span className="etiqueta">Motivo (obligatorio)</span>
        <input
          aria-label="Motivo del paso a vale"
          data-autofoco
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
        />
      </label>
      <label className="campo">
        <span className="etiqueta">Motivo del vale</span>
        <select
          aria-label="Motivo del vale"
          value={valeMotivo}
          onChange={(e) => setValeMotivo(e.target.value as MotivoVale)}
        >
          {MOTIVOS_VALE.map(([v, n]) => (
            <option key={v} value={v}>
              {n}
            </option>
          ))}
        </select>
      </label>
      <label className="campo-check">
        <input
          type="checkbox"
          aria-label="Reutilizar el folio"
          checked={reutilizar}
          onChange={(e) => setReutilizar(e.target.checked)}
        />{' '}
        Reutilizar el folio{folio ? ` ${folio}` : ''} en la próxima boleta
      </label>
      {reutilizar && (
        <label className="campo-check">
          <input
            type="checkbox"
            aria-label="El cliente no se llevó la boleta"
            checked={noEntregada}
            onChange={(e) => setNoEntregada(e.target.checked)}
          />{' '}
          Confirmo que el cliente <b>NO se llevó</b> la boleta impresa (si se la llevó, desmarca
          «Reutilizar el folio»)
        </label>
      )}
      {r && r.estado !== 'COMPLETADO' && (
        <p className="aviso-linea tono-aviso">
          {r.estado === 'RECHAZADO'
            ? 'La boleta ya había salido al SII: se corrige con una nota de crédito.'
            : 'La anulación quedó pedida; vuelve a apretar «Pasar a vale» en unos segundos para terminar.'}
        </p>
      )}
      {!!pasar.error && <PanelError error={pasar.error} />}
    </Modal>
  );
}

/** 0121: lo que devuelve la anulación de una venta duplicada. */
interface ResultadoAnulacionVenta {
  estado: string;
  foliosLiberados?: Array<{ tipoDte: number; folio: number }>;
  valesAnulados?: string[];
  reversas?: Array<{ despacho: string; reversa: string }>;
  pagosDesaplicados?: number;
  recibosAnulados?: string[];
  recibosPendientes?: Array<{ recibo: string; codigo: string }>;
}

/**
 * 0121 · anular una venta registrada dos veces (su documento todavía no sale al SII): se anula el
 * documento (su folio puede reutilizarse), la mercadería vuelve a la bodega, el pago se desaplica y
 * el recibo y la nota quedan anulados. Permiso ventas.venta.anular_duplicada; todo queda auditado.
 * Con `vale` (Rodrigo, 2026-11-01: «volvió la mercadería») anula el vale de venta: no hay SII ni folio.
 */
export function AnularVentaDuplicada({
  notaId,
  documento,
  vale = false,
  alCerrar,
  alListo,
}: {
  notaId: string;
  /** «Boleta N° 4370 · NV 159», para que se vea qué se anula. */
  documento: string;
  /** La venta está documentada con un vale de venta (documento interno, sin folio del SII). */
  vale?: boolean;
  alCerrar: () => void;
  alListo: () => void;
}) {
  const intentos = useIntentos();
  const [motivo, setMotivo] = useState(
    vale ? 'Volvió la mercadería' : 'Venta registrada dos veces',
  );
  const [reutilizar, setReutilizar] = useState(!vale);
  const [noEntregado, setNoEntregado] = useState(false);
  const anular = useMutation({
    mutationFn: () => {
      const cuerpo = {
        motivo: motivo.trim(),
        reutilizarFolio: reutilizar,
        confirmoNoEntregado: reutilizar && noEntregado,
      };
      return api.post<ResultadoAnulacionVenta>(`/ventas/notas/${notaId}/anular-duplicada`, cuerpo, {
        idempotencia: intentos.clave('anular-duplicada', { notaId, ...cuerpo }),
      });
    },
    onSuccess: () => intentos.cerrar(),
    onError: (e) => intentos.cerrar(e),
  });
  const r = anular.data;
  const listo = r?.estado === 'COMPLETADA';
  const valido = motivo.trim().length >= 3 && (!reutilizar || noEntregado);
  return (
    <Modal
      titulo={vale ? 'Anular vale' : 'Anular venta duplicada'}
      alCerrar={listo ? alListo : alCerrar}
      pie={
        <div className="acciones">
          {listo ? (
            <button type="button" className="primario" onClick={alListo}>
              Cerrar
            </button>
          ) : (
            <>
              <button type="button" onClick={alCerrar}>
                Cancelar (Esc)
              </button>
              <button
                type="button"
                className="primario"
                disabled={!valido || anular.isPending}
                onClick={() => anular.mutate()}
              >
                {anular.isPending ? 'Anulando…' : vale ? 'Anular el vale' : 'Anular la venta'}
              </button>
            </>
          )}
        </div>
      }
    >
      {listo ? (
        <div data-anulacion-venta="completada">
          <p>
            <b>{documento}</b>: la venta quedó anulada.
          </p>
          <table className="grilla compacta" aria-label="Lo que se hizo">
            <tbody>
              <tr>
                <th>Documento</th>
                <td>
                  {r.valesAnulados?.length ? (
                    `Vale N° ${r.valesAnulados.join(', ')} anulado (documento interno, no va al SII)`
                  ) : (
                    <>
                      Anulado antes del envío al SII
                      {r.foliosLiberados?.length
                        ? ` · folio ${r.foliosLiberados.map((f) => f.folio).join(', ')} queda para la próxima emisión`
                        : ' · el folio no se reutiliza'}
                    </>
                  )}
                </td>
              </tr>
              <tr>
                <th>Mercadería</th>
                <td>
                  {r.reversas?.length
                    ? `Vuelve a la bodega (despacho ${r.reversas.map((x) => x.despacho).join(', ')} reversado)`
                    : 'No había salido de la bodega'}
                </td>
              </tr>
              <tr>
                <th>Pago</th>
                <td>
                  {r.recibosAnulados?.length
                    ? `Recibo ${r.recibosAnulados.join(', ')} anulado: la caja del turno ya no lo cuenta`
                    : 'Sin recibo que anular'}
                  {!!r.recibosPendientes?.length && (
                    <span className="tono-aviso">
                      {' '}
                      · recibo {r.recibosPendientes.map((x) => x.recibo).join(', ')} quedó sin
                      aplicar (su turno ya se cerró): lo regulariza tesorería
                    </span>
                  )}
                </td>
              </tr>
              <tr>
                <th>Nota de venta</th>
                <td>Anulada</td>
              </tr>
            </tbody>
          </table>
          <p className="apagado">Queda registrado quién lo hizo, cuándo y por qué (auditoría).</p>
        </div>
      ) : (
        <>
          {vale ? (
            <p>
              <b>{documento}</b> es un documento interno (no va al SII). Se deshace la venta entera:
              el vale se anula, la mercadería vuelve a la bodega, el pago (si tiene) se desaplica y
              el recibo y la nota quedan anulados. Úsalo cuando la mercadería volvió.
            </p>
          ) : (
            <p>
              <b>{documento}</b> todavía no sale al SII. Se deshace la venta entera: el documento se
              anula, la mercadería vuelve a la bodega, el pago se desaplica y el recibo y la nota
              quedan anulados. Úsalo cuando la misma venta se registró dos veces.
            </p>
          )}
          <label className="campo">
            <span className="etiqueta">Motivo</span>
            <input
              aria-label="Motivo de la anulación"
              data-autofoco
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </label>
          {!vale && (
            <label className="campo-check">
              <input
                type="checkbox"
                aria-label="Reutilizar el folio"
                checked={reutilizar}
                onChange={(e) => setReutilizar(e.target.checked)}
              />{' '}
              Reutilizar el folio en la próxima emisión del mismo tipo
            </label>
          )}
          {reutilizar && (
            <label className="campo-check">
              <input
                type="checkbox"
                aria-label="El cliente no se llevó el documento"
                checked={noEntregado}
                onChange={(e) => setNoEntregado(e.target.checked)}
              />{' '}
              Confirmo que el cliente <b>NO se llevó</b> el documento impreso (si se lo llevó,
              desmarca «Reutilizar el folio»)
            </label>
          )}
          {r && !listo && (
            <p className="aviso-linea tono-aviso">
              {r.estado === 'RECHAZADA'
                ? 'El documento ya había salido al SII: la venta se corrige con una nota de crédito.'
                : 'La anulación del documento quedó pedida; vuelve a apretar «Anular la venta» en unos segundos para terminar.'}
            </p>
          )}
          {!!anular.error && <PanelError error={anular.error} />}
        </>
      )}
    </Modal>
  );
}
